import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getShootDays, createShootDay, deleteShootDay, updateShootDay, autoSchedule as autoScheduleService, applyAutoSchedule, getCallSheet } from "@/services/productionService";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Calendar, Plus, Trash2, MapPin, Users, Film, Clock, 
  ChevronLeft, ChevronRight, Loader2, Sparkles, FileText, 
  CheckCircle2, AlertCircle, Clapperboard, Save
} from "lucide-react";


const STATUS_COLORS = {
  planned: { bg: "bg-blue-500/15", text: "text-blue-400", label: "Planned" },
  in_progress: { bg: "bg-amber-500/15", text: "text-amber-400", label: "In Progress" },
  completed: { bg: "bg-emerald-500/15", text: "text-emerald-400", label: "Completed" },
  cancelled: { bg: "bg-red-500/15", text: "text-red-400", label: "Cancelled" },
};

function ShootDayCard({ day, isSelected, onClick, onDelete }) {
  const status = STATUS_COLORS[day.status] || STATUS_COLORS.planned;
  return (
    <motion.div
      whileHover={{ scale: 1.01 }}
      onClick={onClick}
      className={`p-4 rounded-xl border cursor-pointer transition-all ${
        isSelected
          ? "border-[var(--color-gold-500)] bg-[rgba(212,168,83,0.05)]"
          : "border-[var(--border-subtle)] bg-[var(--bg-elevated)] hover:border-[var(--border-strong)]"
      }`}
      data-testid={`shoot-day-card-${day.id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <Calendar size={14} className="text-[var(--color-gold-400)]" />
          <span className="text-sm font-medium text-[var(--text-primary)]">{day.date}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${status.bg} ${status.text}`}>
            {status.label}
          </span>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(day.id); }}
            className="opacity-0 group-hover:opacity-100 text-[var(--text-disabled)] hover:text-[var(--color-error)] transition-all"
            data-testid={`delete-day-${day.id}`}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      <h4 className="text-sm font-medium text-[var(--text-primary)] truncate mb-2">{day.title || "Untitled"}</h4>
      <div className="flex flex-wrap gap-2 text-[10px] text-[var(--text-muted)]">
        {day.location_name && (
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-[var(--bg-muted)]">
            <MapPin size={10} /> {day.location_name.substring(0, 25)}
          </span>
        )}
        {day.scenes?.length > 0 && (
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-[var(--bg-muted)]">
            <Film size={10} /> {day.scenes.length} scene{day.scenes.length > 1 ? "s" : ""}
          </span>
        )}
        {day.cast?.length > 0 && (
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-[var(--bg-muted)]">
            <Users size={10} /> {day.cast.length} cast
          </span>
        )}
        <span className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-[var(--bg-muted)]">
          <Clock size={10} /> {day.start_time}-{day.end_time}
        </span>
      </div>
    </motion.div>
  );
}

export default function SchedulePage({ projectId, project, script, characters, locations }) {
  const [shootDays, setShootDays] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [autoSchedule, setAutoSchedule] = useState(null);
  const [autoLoading, setAutoLoading] = useState(false);
  const [callSheet, setCallSheet] = useState(null);
  const [newDay, setNewDay] = useState({
    date: new Date().toISOString().split("T")[0],
    title: "",
    location_name: "",
    location_id: null,
    scenes: [],
    cast: [],
    notes: "",
    start_time: "06:00",
    end_time: "18:00",
  });

  const fetchShootDays = useCallback(async () => {
    try {
      const data = await getShootDays(projectId);
      setShootDays(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchShootDays(); }, [fetchShootDays]);

  const handleCreateDay = async () => {
    try {
      const data = await createShootDay({ ...newDay, project_id: projectId });
      setShootDays([...shootDays, data]);
      setShowAddDialog(false);
      setNewDay({ date: new Date().toISOString().split("T")[0], title: "", location_name: "", location_id: null, scenes: [], cast: [], notes: "", start_time: "06:00", end_time: "18:00" });
      toast.success("Shoot day created");
    } catch (err) {
      toast.error("Failed to create shoot day");
    }
  };

  const handleDeleteDay = async (dayId) => {
    try {
      await deleteShootDay(dayId);
      setShootDays(shootDays.filter(d => d.id !== dayId));
      if (selectedDay?.id === dayId) { setSelectedDay(null); setCallSheet(null); }
      toast.success("Shoot day deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const handleUpdateDay = async (updates) => {
    if (!selectedDay) return;
    try {
      const data = await updateShootDay(selectedDay.id, updates);
      setShootDays(shootDays.map(d => d.id === selectedDay.id ? data : d));
      setSelectedDay(data);
      toast.success("Updated");
    } catch { toast.error("Failed to update"); }
  };

  const handleAutoSchedule = async () => {
    setAutoLoading(true);
    try {
      const data = await autoScheduleService(projectId);
      setAutoSchedule(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Auto-schedule failed");
    } finally { setAutoLoading(false); }
  };

  const handleApplyAutoSchedule = async () => {
    if (!autoSchedule) return;
    try {
      const data = await applyAutoSchedule(projectId, {
        schedule: autoSchedule.suggested_schedule,
        start_date: new Date().toISOString().split("T")[0],
      });
      toast.success(`Created ${data.created} shoot days`);
      setAutoSchedule(null);
      fetchShootDays();
    } catch { toast.error("Failed to apply schedule"); }
  };

  const handleViewCallSheet = async (dayId) => {
    try {
      const data = await getCallSheet(dayId);
      setCallSheet(data);
    } catch { toast.error("Failed to load call sheet"); }
  };

  const scenes = script?.scenes || [];

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="animate-spin text-[var(--color-gold-400)]" size={32} />
      </div>
    );
  }

  return (
    <div className="h-full flex" data-testid="schedule-page">
      {/* Left: Shoot Days List */}
      <div className="w-80 border-r border-[var(--border-subtle)] flex flex-col bg-[var(--bg-surface)]">
        <div className="p-4 border-b border-[var(--border-subtle)]">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-[var(--text-primary)]">Shoot Schedule</h2>
            <span className="text-xs text-[var(--text-muted)]">{shootDays.length} day{shootDays.length !== 1 ? "s" : ""}</span>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowAddDialog(true)} className="btn-primary flex-1 text-xs h-8" size="sm" data-testid="add-shoot-day-btn">
              <Plus size={14} className="mr-1" /> Add Day
            </Button>
            <Button onClick={handleAutoSchedule} disabled={autoLoading} className="btn-secondary flex-1 text-xs h-8" size="sm" data-testid="auto-schedule-btn">
              {autoLoading ? <Loader2 size={14} className="animate-spin mr-1" /> : <Sparkles size={14} className="mr-1" />}
              Auto Plan
            </Button>
          </div>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-3 space-y-2">
            {shootDays.length === 0 ? (
              <div className="text-center py-12 text-[var(--text-muted)]">
                <Calendar size={32} className="mx-auto mb-3 opacity-40" />
                <p className="text-sm">No shoot days yet</p>
                <p className="text-xs mt-1">Add manually or use Auto Plan</p>
              </div>
            ) : (
              shootDays.map(day => (
                <ShootDayCard
                  key={day.id}
                  day={day}
                  isSelected={selectedDay?.id === day.id}
                  onClick={() => { setSelectedDay(day); handleViewCallSheet(day.id); }}
                  onDelete={handleDeleteDay}
                />
              ))
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Right: Detail / Call Sheet */}
      <div className="flex-1 overflow-y-auto bg-[var(--bg-app)]">
        {autoSchedule ? (
          <div className="p-6 max-w-3xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-[var(--text-primary)]">Auto-Generated Schedule</h3>
                <p className="text-sm text-[var(--text-muted)] mt-1">
                  {autoSchedule.total_days} shoot days for {autoSchedule.total_scenes} scenes
                </p>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setAutoSchedule(null)} className="btn-secondary text-xs h-8" size="sm">Dismiss</Button>
                <Button onClick={handleApplyAutoSchedule} className="btn-primary text-xs h-8" size="sm" data-testid="apply-schedule-btn">
                  <CheckCircle2 size={14} className="mr-1" /> Apply Schedule
                </Button>
              </div>
            </div>
            <div className="space-y-3">
              {autoSchedule.suggested_schedule.map((day, i) => (
                <div key={i} className="p-4 rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-elevated)]">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-[var(--color-gold-400)]">{day.title}</h4>
                    <span className="text-xs text-[var(--text-muted)]">~{day.estimated_hours}h</span>
                  </div>
                  <div className="flex flex-wrap gap-2 text-[10px]">
                    <span className="px-2 py-0.5 rounded bg-[var(--bg-muted)] text-[var(--text-muted)]">
                      Scenes: {day.scenes.join(", ")}
                    </span>
                    {day.cast.length > 0 && (
                      <span className="px-2 py-0.5 rounded bg-[var(--bg-muted)] text-[var(--text-muted)]">
                        Cast: {day.cast.join(", ")}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : selectedDay ? (
          <div className="p-6 max-w-3xl mx-auto">
            {/* Day Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-[var(--text-primary)]" data-testid="selected-day-title">{selectedDay.title || "Shoot Day"}</h3>
                <p className="text-sm text-[var(--text-muted)] mt-1">{selectedDay.date} | {selectedDay.start_time} - {selectedDay.end_time}</p>
              </div>
              <Select value={selectedDay.status} onValueChange={(v) => handleUpdateDay({ status: v })}>
                <SelectTrigger className="w-32 h-8 text-xs bg-[var(--bg-elevated)] border-[var(--border-subtle)]" data-testid="day-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[var(--bg-surface)] border-[var(--border-default)]">
                  <SelectItem value="planned">Planned</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Editable Fields */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="text-xs text-[var(--text-muted)] mb-1 block">Title</label>
                <Input
                  value={selectedDay.title || ""}
                  onChange={(e) => setSelectedDay({ ...selectedDay, title: e.target.value })}
                  onBlur={() => handleUpdateDay({ title: selectedDay.title })}
                  className="input text-sm h-9"
                  data-testid="day-title-input"
                />
              </div>
              <div>
                <label className="text-xs text-[var(--text-muted)] mb-1 block">Location</label>
                <Input
                  value={selectedDay.location_name || ""}
                  onChange={(e) => setSelectedDay({ ...selectedDay, location_name: e.target.value })}
                  onBlur={() => handleUpdateDay({ location_name: selectedDay.location_name })}
                  className="input text-sm h-9"
                  data-testid="day-location-input"
                />
              </div>
              <div>
                <label className="text-xs text-[var(--text-muted)] mb-1 block">Start Time</label>
                <Input
                  type="time"
                  value={selectedDay.start_time || "06:00"}
                  onChange={(e) => { setSelectedDay({ ...selectedDay, start_time: e.target.value }); handleUpdateDay({ start_time: e.target.value }); }}
                  className="input text-sm h-9"
                />
              </div>
              <div>
                <label className="text-xs text-[var(--text-muted)] mb-1 block">End Time</label>
                <Input
                  type="time"
                  value={selectedDay.end_time || "18:00"}
                  onChange={(e) => { setSelectedDay({ ...selectedDay, end_time: e.target.value }); handleUpdateDay({ end_time: e.target.value }); }}
                  className="input text-sm h-9"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="mb-6">
              <label className="text-xs text-[var(--text-muted)] mb-1 block">Notes</label>
              <Textarea
                value={selectedDay.notes || ""}
                onChange={(e) => setSelectedDay({ ...selectedDay, notes: e.target.value })}
                onBlur={() => handleUpdateDay({ notes: selectedDay.notes })}
                className="input text-sm resize-none h-20"
                placeholder="Weather conditions, special requirements..."
                data-testid="day-notes-input"
              />
            </div>

            {/* Call Sheet */}
            {callSheet && (
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
                  <FileText size={16} className="text-[var(--color-gold-400)]" />
                  Call Sheet
                </h4>

                {/* Cast Section */}
                {callSheet.cast_details?.length > 0 && (
                  <div className="rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-elevated)] p-4">
                    <h5 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-3">Cast ({callSheet.cast_details.length})</h5>
                    <div className="space-y-2">
                      {callSheet.cast_details.map((c, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-[var(--border-subtle)] last:border-0">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-[rgba(212,168,83,0.1)] flex items-center justify-center">
                              <Users size={14} className="text-[var(--color-gold-400)]" />
                            </div>
                            <div>
                              <span className="text-sm text-[var(--text-primary)]">{c.name}</span>
                              {c.description && <p className="text-[10px] text-[var(--text-muted)] truncate max-w-[200px]">{c.description}</p>}
                            </div>
                          </div>
                          {c.has_reference && <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400">Sheet Ready</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Scenes Section */}
                {callSheet.scene_details?.length > 0 && (
                  <div className="rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-elevated)] p-4">
                    <h5 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-3">Scenes ({callSheet.scene_details.length})</h5>
                    <div className="space-y-2">
                      {callSheet.scene_details.map((s, i) => (
                        <div key={i} className="py-2 border-b border-[var(--border-subtle)] last:border-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[rgba(212,168,83,0.1)] text-[var(--color-gold-400)]">
                              Sc. {s.scene_number}
                            </span>
                            <span className="text-sm text-[var(--text-primary)]">{s.heading}</span>
                          </div>
                          <p className="text-xs text-[var(--text-muted)] line-clamp-2 ml-8">{s.content_preview}</p>
                          {s.characters?.length > 0 && (
                            <div className="flex flex-wrap gap-1 ml-8 mt-1">
                              {s.characters.map((ch, ci) => (
                                <span key={ci} className="text-[9px] px-1.5 py-0.5 rounded bg-[var(--bg-muted)] text-[var(--text-muted)]">{ch}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Location */}
                {callSheet.location_detail && (
                  <div className="rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-elevated)] p-4">
                    <h5 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-2">Location</h5>
                    <div className="flex items-center gap-3">
                      <MapPin size={16} className="text-[var(--color-gold-400)]" />
                      <div>
                        <span className="text-sm text-[var(--text-primary)]">{callSheet.location_detail.name}</span>
                        <span className="text-[10px] ml-2 px-1.5 py-0.5 rounded bg-[var(--bg-muted)] text-[var(--text-muted)]">{callSheet.location_detail.type}</span>
                        {callSheet.location_detail.description && (
                          <p className="text-xs text-[var(--text-muted)] mt-1">{callSheet.location_detail.description}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-center">
            <div>
              <Clapperboard size={48} className="mx-auto mb-4 text-[var(--text-disabled)] opacity-40" />
              <p className="text-sm text-[var(--text-muted)]">Select a shoot day or create one</p>
              <p className="text-xs text-[var(--text-disabled)] mt-1">Use Auto Plan to generate a schedule from your script</p>
            </div>
          </div>
        )}
      </div>

      {/* Add Day Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-[var(--bg-surface)] border-[var(--border-default)] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg text-[var(--text-primary)]">Add Shoot Day</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <div>
              <label className="text-xs text-[var(--text-muted)] mb-1 block">Date</label>
              <Input type="date" value={newDay.date} onChange={(e) => setNewDay({ ...newDay, date: e.target.value })} className="input text-sm" data-testid="new-day-date" />
            </div>
            <div>
              <label className="text-xs text-[var(--text-muted)] mb-1 block">Title</label>
              <Input value={newDay.title} onChange={(e) => setNewDay({ ...newDay, title: e.target.value })} className="input text-sm" placeholder="Day 1 - Studio A" data-testid="new-day-title" />
            </div>
            <div>
              <label className="text-xs text-[var(--text-muted)] mb-1 block">Location</label>
              <Select value={newDay.location_id || "__custom"} onValueChange={(v) => {
                if (v === "__custom") {
                  setNewDay({ ...newDay, location_id: null, location_name: "" });
                } else {
                  const loc = locations.find(l => l.id === v);
                  setNewDay({ ...newDay, location_id: v, location_name: loc?.name || "" });
                }
              }}>
                <SelectTrigger className="input text-sm">
                  <SelectValue placeholder="Select location..." />
                </SelectTrigger>
                <SelectContent className="bg-[var(--bg-surface)] border-[var(--border-default)]">
                  <SelectItem value="__custom">Custom / Other</SelectItem>
                  {locations.map(loc => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!newDay.location_id && (
                <Input value={newDay.location_name} onChange={(e) => setNewDay({ ...newDay, location_name: e.target.value })} className="input text-sm mt-2" placeholder="Enter location name..." />
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[var(--text-muted)] mb-1 block">Start</label>
                <Input type="time" value={newDay.start_time} onChange={(e) => setNewDay({ ...newDay, start_time: e.target.value })} className="input text-sm" />
              </div>
              <div>
                <label className="text-xs text-[var(--text-muted)] mb-1 block">End</label>
                <Input type="time" value={newDay.end_time} onChange={(e) => setNewDay({ ...newDay, end_time: e.target.value })} className="input text-sm" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[var(--text-muted)] mb-1 block">Scenes</label>
              <div className="flex flex-wrap gap-1.5 p-2 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-elevated)] max-h-24 overflow-y-auto">
                {scenes.map((s, i) => {
                  const selected = newDay.scenes.includes(i + 1);
                  return (
                    <button
                      key={i}
                      onClick={() => {
                        setNewDay({
                          ...newDay,
                          scenes: selected ? newDay.scenes.filter(n => n !== i + 1) : [...newDay.scenes, i + 1]
                        });
                      }}
                      className={`text-[10px] px-2 py-1 rounded-md transition-all ${
                        selected ? "bg-[rgba(212,168,83,0.2)] text-[var(--color-gold-400)] border border-[rgba(212,168,83,0.3)]" : "bg-[var(--bg-muted)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                      }`}
                    >
                      {i + 1}
                    </button>
                  );
                })}
                {scenes.length === 0 && <span className="text-[10px] text-[var(--text-disabled)]">No scenes in script</span>}
              </div>
            </div>
            <Button onClick={handleCreateDay} className="btn-primary w-full text-sm" data-testid="create-day-submit">
              <Plus size={14} className="mr-1" /> Create Shoot Day
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
